import React, { useEffect, useState } from "react";
import { useRef } from "react";

import TeamMembersRow from "../../../projects/publicView/components/teamMembersRow/teamMembersRow";
import ArrowButton from "../fireButton/ArrowButton";
import CommentButton from "../comments/CommentButton";
import CommentPopover from "../comments/CommentPopover";

const CATEGORY_COLORS = {
  Tech: "#4D8EFF",
  Art: "#68B266",
  Music: "#A162E8",
  Science: "#45B9B0",
  Content: "#D58D49",
  Fashion: "#FF8BA7",
  Gaming: "#FF7070",
  Food: "#FFA500",
  Craft: "#A084E8",
};

const getBadgeColor = (category) => CATEGORY_COLORS[category] || "#1e1e1e";

const CardMid = ({ project, onClick, user }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [projectOwner, setProjectOwner] = useState(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comments, setComments] = useState([]);
  const commentButtonRef = useRef();

  useEffect(() => {
      const fetchOwnerData = async () => {
        if (!project?.owner) return;
        try {
          // console.log("📡 Fetching owner details...");
          const encodedEmail = encodeURIComponent(project.owner);
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/${encodedEmail}`);
          const data = await res.json();
          if (res.ok && data) {
            setProjectOwner(data); // store entire owner object
            // console.log("✅ Owner data fetched:", data);
          } else {
            console.warn("⚠️ Owner data not found:", data);
          }
        } catch (err) {
          console.error("❌ Error fetching owner data:", err.message);
        }
      };
      fetchOwnerData();
    }, [project.owner]);

  useEffect(() => {
      const fetchComments = async () => {
        try {
          // console.log("📡 Fetching comments...");
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/engagements/comments/${project.id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          const data = await res.json();
          if (res.ok) {
            // console.log("✅ Comments fetched:", data.comments);
            setComments(data.comments || []);
          } else {
            console.warn("⚠️ Comment fetch failed:", data);
          }
        } catch (err) {
          console.error("❌ Error fetching comments:", err.message);
        }
      };
  
      if (showCommentBox) fetchComments();
    }, [showCommentBox, project.id]);
  
    
  const handleAddComment = async (text) => {
  const trimmed = text.trim();
  if (!trimmed) return;

  setComments((prev) => [...prev, trimmed]);
  console.log("📤 Posting comment:", trimmed);

  try {
    const token = localStorage.getItem("token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/engagements/comments/${project.id}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        text: trimmed,
        firstName: user?.firstName,
      }),
    });

    if (!res.ok) {
      console.error("❌ Failed to post comment:", await res.text());
    } else {
      console.log("✅ Comment posted");

      // ✅ Only notify the project owner if the commenter is *not* the owner
      if (user?.email !== project.owner) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/notifications`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            recipientEmail: project.owner, // send to owner
            message: `${user?.firstName} left a comment under your project: ${project.title}`,
            type: "comment_by",
            actor: user?.email,
            projectId: project.id,
            projectTitle: project.title,
          }),
        });
      } else {
        console.log("🔕 No notification: user is the project owner.");
      }
    }
  } catch (err) {
    console.error("❌ Network error posting comment:", err.message);
  }
};

  return (
    <>
      <div className="w-96 bg-white rounded-2xl overflow-hidden" style={{ height: "16.8rem" }}>
      <div
        className={`w-full h-full p-3 flex flex-col cursor-pointer transition-transform duration-200 ${
          isHovered ? "scale-[1.02] shadow-lg" : "shadow-md"
        }`}
        onClick={() => onClick(project.id)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Badge + Created by */}
        <div className="flex justify-between items-center mb-1">
          <div
            className="px-2 py-[2px] h-5 rounded text-[9px] font-medium uppercase"
            style={{ backgroundColor: `${getBadgeColor(project.category)}33` }}
          >
            {project.category}
          </div>
          <div className="text-[9px] text-gray-500 text-right leading-tight">
            <span className="font-medium">By</span>{" "}
            <span className="text-[#0D062D] font-semibold">
  {projectOwner ? `${projectOwner.firstName} ${projectOwner.lastName}` : ""}
</span>

          </div>
        </div>

        {/* Title */}
        <div className="text-[#0D062D] text-sm font-semibold truncate w-full mb-2">
          {project.title}
        </div>

        {/* Image */}
        <div className="w-full h-[5.5rem] overflow-hidden rounded-lg mb-2">
          <img
            src={project.image || "https://placehold.co/306x88"}
            alt={project.title}
            className="w-full h-full object-contain rounded-lg"
          />
        </div>

        {/* One-liner */}
        <div className="text-[#787486] text-xs font-medium leading-snug mb-2 truncate w-full">
          {project.oneLiner}
        </div>

        {/* Footer */}
          <div className="mt-2 flex items-center justify-between w-full">
            {/* Team members with max width */}
            <div className="max-w-[100px] overflow-hidden">
              <TeamMembersRow projectId={project.id} />
            </div>

            {/* Centered comment */}
            <div className="flex items-center justify-center min-w-[48px]">
              <CommentButton
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCommentBox((prev) => !prev);
                }}
                ref={commentButtonRef}
                count={project.commentCount}
              />
            </div>

            {/* Arrow */}
            <div className="flex items-center min-w-[48px]">
              <ArrowButton
                projectId={project.id}
                user={user}
                initialFireCount={project.fireCount}
              />
            </div>
          </div>
      </div>
    </div>
    {/* 🧠 Comment Popover */}
    {showCommentBox && commentButtonRef.current && (
      <CommentPopover
        anchorRef={commentButtonRef}
        comments={comments}
        onAddComment={handleAddComment}
        onClose={() => {
          console.log("❎ Comment box closed");
          setShowCommentBox(false);
        }}
      />
    )}
    </>
  );
};

export default CardMid;
